import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Briefcase, Building2, CalendarDays, CheckCircle2, Clock, GraduationCap, Laptop, MapPin, Wallet, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { QuickApplyDialog } from "@/components/apply/QuickApplyDialog";
import { ShareButton } from "@/components/common/ShareButton";
import { LiveBadge } from "@/components/jobs/LiveBadge";
import { api } from "@/lib/api";
import { formatSalary } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const DetailChip = ({ icon: Icon, label, value }) => (
  <Card className="rounded-xl border-border shadow-none">
    <CardContent className="p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium mt-1 flex items-center gap-1.5 text-sm">
        <Icon className="h-4 w-4 text-primary shrink-0" /> {value || "—"}
      </p>
    </CardContent>
  </Card>
);

const ListSection = ({ title, items }) => {
  if (!items?.length) return null;
  return (
    <div className="mt-8">
      <h2 className="font-heading font-semibold text-lg">{title}</h2>
      <ul className="mt-3 space-y-2.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm sm:text-base text-muted-foreground">
            <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" /> {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function JobDetail() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${jobId}`).then((res) => setJob(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [jobId]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-4">
        <Skeleton className="h-10 w-2/3 rounded-lg" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <p className="font-heading font-semibold text-xl">Job not found</p>
        <Link to="/jobs"><Button variant="outline" className="mt-4 rounded-lg bg-white">Back to jobs</Button></Link>
      </div>
    );
  }

  const canApply = job.status === "Active" && (!user || user.role === "member");

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/jobs" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="job-detail-back-link">
        <ArrowLeft className="h-4 w-4" /> All jobs
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start gap-5">
        <span className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Briefcase className="h-7 w-7" />
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge variant="outline" className="rounded-full text-xs bg-blue-50 text-blue-700 border-blue-200">
              <Building2 className="h-3 w-3 mr-1" /> {job.hiring_brand}
            </Badge>
            <LiveBadge status={job.status} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight" data-testid="job-detail-title">{job.title}</h1>
          <p className="text-sm text-emerald-700 font-medium mt-1.5">{formatSalary(job.salary_min, job.salary_max)}</p>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          <ShareButton
            url={`${window.location.origin}/jobs/${job.id}`}
            text={`${job.title} at ${job.hiring_brand} – Apply now:`}
            testIdPrefix="job-detail-share"
            size="lg"
          />
          {canApply && (
            <Button size="lg" className="rounded-lg shrink-0" onClick={() => setApplyOpen(true)} data-testid="job-detail-apply-button">
              I'm Interested
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
        <DetailChip icon={MapPin} label="Location" value={job.location} />
        <DetailChip icon={Zap} label="Experience" value={job.experience} />
        <DetailChip icon={GraduationCap} label="Education" value={job.education} />
        <DetailChip icon={Clock} label="Shift" value={job.shift} />
        <DetailChip icon={CalendarDays} label="Working Days" value={job.working_days} />
        <DetailChip icon={Briefcase} label="Employment Type" value={job.employment_type} />
        <DetailChip icon={Laptop} label="Work Mode" value={job.work_mode} />
        <DetailChip icon={Wallet} label="Salary" value={formatSalary(job.salary_min, job.salary_max)} />
      </div>

      {job.about && (
        <div className="mt-8">
          <h2 className="font-heading font-semibold text-lg">About the role</h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-2 leading-relaxed whitespace-pre-line">{job.about}</p>
        </div>
      )}

      <ListSection title="Responsibilities" items={job.responsibilities} />
      <ListSection title="Requirements" items={job.requirements} />
      <ListSection title="Benefits" items={job.benefits} />

      {canApply && (
        <div className="rounded-xl bg-primary/5 border border-primary/15 p-6 text-center mt-10">
          <p className="font-heading font-semibold">Interested? Apply in under 30 seconds.</p>
          <p className="text-sm text-muted-foreground mt-1">No resume needed — just your name and mobile number.</p>
          <Button className="rounded-lg mt-4" size="lg" onClick={() => setApplyOpen(true)} data-testid="job-detail-bottom-apply-button">
            I'm Interested
          </Button>
        </div>
      )}

      <QuickApplyDialog job={job} open={applyOpen} onOpenChange={setApplyOpen} />
    </div>
  );
}
